import {
  getSimplePrettyTerminal,
  moonlight,
} from '@loglayer/transport-simple-pretty-terminal';
import {
  ConsoleTransport,
  type ILogLayer,
  LogLayer,
  type PluginBeforeMessageOutParams,
} from 'loglayer';

type Logger = {
  debug: (message: string, meta?: unknown) => void;
  info: (message: string, meta?: unknown) => void;
  warn: (message: string, meta?: unknown) => void;
  error: (message: string, meta?: unknown) => void;
};

/**
 * Edge Runtime does not support Node.js fs APIs.
 * Fall back to console in that environment.
 */
const isEdgeRuntime = process.env['NEXT_RUNTIME'] === 'edge';

// Detect if we're on the server or client
const isServer = typeof window === 'undefined';

// ANSI color codes — no extra dependency needed
const COLORS = {
  debug: '\x1b[36m', // cyan
  info: '\x1b[32m', // green
  warn: '\x1b[33m', // yellow
  error: '\x1b[31m', // red
  reset: '\x1b[0m',
  dim: '\x1b[2m',
} as const;

type ColorLevel = 'debug' | 'info' | 'warn' | 'error';

function colorize(level: string, message: string): string {
  const color = COLORS[level as ColorLevel] ?? '';
  const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
  return `${COLORS.dim}${now}${COLORS.reset} ${color}${level.toUpperCase().padEnd(5)}${COLORS.reset} ${message}`;
}

function createLogLayerLogger(): LogLayer {
  const logLevel = (process.env['LOG_LEVEL'] ?? 'info') as
    | 'trace'
    | 'debug'
    | 'info'
    | 'warn'
    | 'error'
    | 'fatal';

  const transport = new ConsoleTransport({
    level: logLevel,
    enabled: process.env['NODE_ENV'] !== 'development', // Disable in development to use pretty terminal
    logger: console,
  });

  const log = new LogLayer({
    transport: [
      transport,
      getSimplePrettyTerminal({
        enabled: process.env['NODE_ENV'] === 'development',
        level: logLevel,
        runtime: isServer ? 'node' : 'browser',
        viewMode: 'expanded', // "inline" | "message-only" | "expanded"
        theme: moonlight,
      }),
    ],
    plugins: [
      {
        // Add a plugin to label the log entry as coming from the server or client
        onBeforeMessageOut(params: PluginBeforeMessageOutParams) {
          const tag = isServer ? 'Server' : 'Client';

          if (params.messages && params.messages.length > 0) {
            if (typeof params.messages[0] === 'string') {
              params.messages[0] = `[${tag}] ${params.messages[0]}`;
            }
          }

          return params.messages;
        },
      },
    ],
  });

  return log;
}

function createConsoleLogger(): ILogLayer {
  const shim: Logger = {
    debug: (message: string, meta?: unknown) =>
      console.debug(colorize('debug', message), meta ?? ''),
    info: (message: string, meta?: unknown) =>
      console.info(colorize('info', message), meta ?? ''),
    warn: (message: string, meta?: unknown) =>
      console.warn(colorize('warn', message), meta ?? ''),
    error: (message: string, meta?: unknown) =>
      console.error(colorize('error', message), meta ?? ''),
  };
  return shim as unknown as ILogLayer;
}

export const logger: ILogLayer = isEdgeRuntime
  ? createConsoleLogger()
  : createLogLayerLogger();
